import { FC } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowSquareOutIcon } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@libs/zapi-ui/shadcn";
import { FigmaTypography } from "@libs/zapi-ui/custom";
import { AppLoader } from "../AppLoader";
import { getDeficiency, type AdminDeficiency } from "../../services/deficiencies";
import { STATUS_LABELS, STATUS_STYLES, SEVERITY_STYLES, TYPE_LABELS, caseBriefUrl, chapterUrl } from "./deficiencyDisplay";

const Field: FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex flex-col gap-1">
    <div className="text-[12px] uppercase tracking-wide text-gray-400">{label}</div>
    <div className="text-[14px] leading-[20px] text-gray-800">{children}</div>
  </div>
);

const Body: FC<{ deficiency: AdminDeficiency }> = ({ deficiency: d }) => {
  const sourceUrl =
    d.subject === "case_brief"
      ? d.caseBriefId
        ? caseBriefUrl(d.caseBriefId)
        : null
      : d.projectId && d.chapterAId
        ? chapterUrl(d.projectId, d.chapterAId)
        : null;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`rounded-full border px-2 py-0.5 text-[12px] leading-[18px] font-medium ${SEVERITY_STYLES[d.severity]}`}>
          {d.severity}
        </span>
        <span className={`rounded-full border px-2 py-0.5 text-[12px] leading-[18px] font-medium ${STATUS_STYLES[d.status]}`}>
          {STATUS_LABELS[d.status]}
        </span>
        <span className="rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-[12px] leading-[18px] text-gray-600">
          {d.typeLabel ?? TYPE_LABELS[d.type] ?? d.type}
        </span>
        <span className="text-[12px] text-gray-400">
          {d.detectedBy === "ai" ? "Detected by AI" : `Flagged by ${d.detectedByUserName ?? "a reviewer"}`}
        </span>
      </div>

      <Field label="Description">{d.description}</Field>

      {d.rootCause && <Field label="Root cause">{d.rootCause}</Field>}
      {d.fixNeeds && <Field label="Fix needs">{d.fixNeeds}</Field>}

      <Field label={d.subject === "case_brief" ? "Flagged case brief text" : "Flagged chapter text"}>
        <blockquote className="border-l-2 border-gray-200 pl-3 italic text-gray-600">{d.chapterAQuote}</blockquote>
      </Field>

      {d.chapterBQuote && (
        <Field label="Conflicting text">
          <blockquote className="border-l-2 border-gray-200 pl-3 italic text-gray-600">{d.chapterBQuote}</blockquote>
        </Field>
      )}

      <Field label="Source">
        <div className="flex flex-wrap items-center gap-2 text-[13px] text-gray-600">
          <span>{d.caseBriefName ?? d.projectName ?? "—"}</span>
          {d.organizationName && <span className="text-gray-400">· {d.organizationName}</span>}
          {sourceUrl && (
            <a
              href={sourceUrl}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1 text-blue-600 hover:underline"
            >
              Open
              <ArrowSquareOutIcon size={11} className="opacity-60" />
            </a>
          )}
        </div>
      </Field>
    </div>
  );
};

type TProps = {
  deficiencyId: number | null;
  onClose: () => void;
};

export const DeficiencyDialog: FC<TProps> = ({ deficiencyId, onClose }) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin-deficiency", deficiencyId],
    queryFn: () => getDeficiency(deficiencyId as number),
    enabled: deficiencyId !== null
  });

  return (
    <Dialog open={deficiencyId !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Deficiency #{deficiencyId}</DialogTitle>
        </DialogHeader>
        {isLoading ? (
          <AppLoader />
        ) : isError || !data ? (
          <FigmaTypography variant="body-2" className="text-red-600">
            Failed to load the deficiency.
          </FigmaTypography>
        ) : (
          <Body deficiency={data} />
        )}
      </DialogContent>
    </Dialog>
  );
};
